import React, { useMemo } from "react";
import { Text, Box, Skeleton, RingProgress } from "@mantine/core";
import { Card } from "@xplatform/design-system";
import { LineChart } from "@mantine/charts";
import type { ProcessTimeSeriesViewModel } from "@models/monitoring";
import "./ProcessTimeSeriesChart.scss";

interface ProcessTimeSeriesChartProps {
    data: ProcessTimeSeriesViewModel | null;
    isLoading: boolean;
}

const ProcessTimeSeriesChart: React.FC<ProcessTimeSeriesChartProps> = ({
    data,
    isLoading,
}) => {
    const chartData = useMemo(() => {
        if (!data) return [];

        return data.dataPoints.map((point) => ({
            timestamp: new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
            }).format(point.timestamp),
            Total: point.totalProcesses,
            Completed: point.completedProcesses,
            Failed: point.failedProcesses,
        }));
    }, [data]);

    const latestCompletion =
        data && data.dataPoints.length > 0
            ? data.dataPoints[data.dataPoints.length - 1].percentage
            : 0;

    if (isLoading) {
        return (
            <Card>
                <Skeleton height={32} width="40%" mb="sm" />
                <Skeleton height={320} mt="sm" />
            </Card>
        );
    }

    if (!data || chartData.length === 0) {
        return (
            <Card withBorder>
                <Text fw={600} size="lg" mb={4}>
                    Process Trend
                </Text>
                <Text>No data available. Select a date range to view process trends.</Text>
            </Card>
        );
    }

    return (
        <Card className="process-time-series-card" withBorder>
            <Box className="process-time-series-card__header">
                <Box>
                    <Text fw={600} size="lg" mb={4}>
                        Process Trend
                    </Text>
                    <Text size="sm" c="dimmed">
                        Total, completed, and failed processes by day
                    </Text>
                </Box>
                <Box className="process-time-series-card__indicator">
                    <RingProgress
                        size={88}
                        thickness={10}
                        roundCaps
                        sections={[{ value: latestCompletion, color: "teal" }]}
                        label={
                            <Text ta="center" fw={700} size="sm">
                                {Math.round(latestCompletion)}%
                            </Text>
                        }
                    />
                    <Text size="xs" c="dimmed" ta="center">
                        Latest completion
                    </Text>
                </Box>
            </Box>
            <LineChart
                h={320}
                data={chartData}
                dataKey="timestamp"
                series={[
                    { name: "Completed", color: "teal.6" },
                    { name: "Failed", color: "red.6" },
                ]}
                curveType="monotone"
                withLegend
                withDots
                yAxisProps={{ allowDecimals: false }}
                legendProps={{
                    verticalAlign: 'bottom',
                    align: 'center',
                    height: 36
                }}
            />
        </Card>
    );
};

export default ProcessTimeSeriesChart;